import type { RequestConfig } from "@app/config/axios/http";
import { boApi } from "@src/api/bo";
import type { ApiOmpBoState } from "@src/api/bo";
import { divisionApi, divisionServices } from "@src/api/dictionaries/divisions";
import type { ApiDivision } from "@src/api/dictionaries/divisions/types";
import { ordersApi, type ApiOrder } from "@src/api/orders";
import { stockDepotCardsApi, type ApiStockDepotCard } from "@src/api/stock/stockDepotCards";
import { DepotCardType } from "@src/types/business/depotCardType";
import { wsDepotCardsServices, type WsDepotCard, type WsDepotCardExtends } from "../wsDepotCards";
import { wsInplantsApi } from "./api.core";
import type {
    ApiWsInplant,
    ApiWsInplantContent,
    ApiWsInplantFilter,
    WsInplant,
    WsInplantContent,
    WsInplantContentExtends,
    WsInplantContentFilter,
    WsInplantExtends,
} from "./types";

/**
 * Сервисный слой — ВЗН УП: обогащение заголовка и состава связанными сущностями.
 */
export const wsInplantsServices = {
    ...wsInplantsApi,

    /**
     * Загружает документы ВЗН и обогащает их подразделениями, участками и состоянием БО.
     */
    load: async (
        params: {
            flt?: ApiWsInplantFilter;
            include?: Array<keyof WsInplantExtends>;
            includeBo?: Array<"State">;
        },
        config?: RequestConfig,
    ): Promise<WsInplant[]> => {
        const { flt, include, includeBo } = params;

        const inplants = await wsInplantsApi.load({ flt }, config);

        if (include == null) {
            return inplants.map((item) => serializeInplant(item));
        }

        const divisionCodes = new Set<number>();
        const sectionCodes = new Set<number>();
        const boStateCodes = new Set<number>();

        const includeSender = include.includes("Sender");
        const includeReceiver = include.includes("Receiver");
        const includeSenderSection = include.includes("SenderSection");
        const includeReceiverSection = include.includes("ReceiverSection");
        const includeBoData = include.includes("Bo");
        const includeBoState = includeBoData && (includeBo?.includes("State") ?? false);

        inplants.forEach((inp) => {
            if (includeSender) divisionCodes.add(inp.Sender);
            if (includeReceiver) divisionCodes.add(inp.Receiver);
            if (includeSenderSection && inp.SenderSection !== 0) sectionCodes.add(inp.SenderSection);
            if (includeReceiverSection && inp.ReceiverSection !== 0) sectionCodes.add(inp.ReceiverSection);
            if (includeBoState) boStateCodes.add(inp.bo.State);
        });

        const divisions = new Map<number, ApiDivision>();
        const sections = new Map<number, ApiDivision>();
        const boStates = new Map<number, ApiOmpBoState>();

        const promises: Promise<unknown>[] = [];

        if ((includeSender || includeReceiver) && divisionCodes.size > 0) {
            promises.push(
                divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config).then(({ items }) => {
                    items.forEach((div) => divisions.set(div.Code, div));
                }),
            );
        }

        if ((includeSenderSection || includeReceiverSection) && sectionCodes.size > 0) {
            promises.push(
                divisionServices.loadBySectionCodes({ sectionCodes: [...sectionCodes] }, config).then((items) => {
                    items.forEach((item) => {
                        if (item.SectionCode !== 0) {
                            sections.set(item.SectionCode, item);
                        }
                    });
                }),
            );
        }

        if (includeBoState && boStateCodes.size > 0) {
            promises.push(
                boApi.states.load({ Codes: [...boStateCodes] }, config).then((items) => {
                    items.forEach((state) => boStates.set(state.Code, state));
                }),
            );
        }

        await Promise.all(promises);

        return inplants.map<WsInplant>((inp) =>
            serializeInplant(inp, {
                Sender: includeSender ? divisions.get(inp.Sender) : undefined,
                SenderSection: includeSenderSection ? (sections.get(inp.SenderSection) ?? null) : undefined,
                Receiver: includeReceiver ? divisions.get(inp.Receiver) : undefined,
                ReceiverSection: includeReceiverSection ? (sections.get(inp.ReceiverSection) ?? null) : undefined,
                Bo: {
                    StateCode: inp.bo.State,
                    State: includeBoState ? boStates.get(inp.bo.State) : undefined,
                },
            }),
        );
    },

    /** Возвращает один документ ВЗН по фильтру. */
    loadOne: async (
        params: {
            flt: ApiWsInplantFilter;
            include?: Array<keyof WsInplantExtends>;
            includeBo?: Array<"State">;
        },
        config?: RequestConfig,
    ): Promise<WsInplant | null> => {
        const items = await wsInplantsServices.load(params, config);

        return items[0] ?? null;
    },

    content: {
        ...wsInplantsApi.content,

        /** Загружает состав ВЗН и при необходимости догружает карточки и заказы. */
        load: async (
            params: {
                flt?: WsInplantContentFilter;
                include?: Array<keyof WsInplantContentExtends>;
                includeLeaveCard?: Array<keyof WsDepotCardExtends>;
            },
            config?: RequestConfig,
        ): Promise<WsInplantContent[]> => {
            const { flt, include, includeLeaveCard } = params;

            const inpContents = await wsInplantsApi.content.load({ flt }, config);

            if (include == null) {
                return inpContents.map((item) => serializeContent(item));
            }

            const stockDepotCardCodes = new Set<number>();
            const wsDepotCardCodes = new Set<number>();
            const orderCodes = new Set<number>();

            const includeArrivalCard = include.includes("ArrivalCard");
            const shouldIncludeLeaveCard = include.includes("LeaveCard");
            const includeOrder = include.includes("Order");

            inpContents.forEach((inp) => {
                if (shouldIncludeLeaveCard && inp.LeaveCardCode !== 0) {
                    wsDepotCardCodes.add(inp.LeaveCardCode);
                }

                if (includeArrivalCard && inp.ArrivalCardCode !== 0) {
                    if (inp.ArrivalCardType === DepotCardType.Ws) {
                        wsDepotCardCodes.add(inp.ArrivalCardCode);
                    } else {
                        stockDepotCardCodes.add(inp.ArrivalCardCode);
                    }
                }

                if (includeOrder && inp.OrderCode !== 0) {
                    orderCodes.add(inp.OrderCode);
                }
            });

            const stockDepotCards = new Map<number, ApiStockDepotCard>();
            const wsDepotCards = new Map<number, WsDepotCard>();
            const orders = new Map<number, ApiOrder>();

            if (includeArrivalCard && stockDepotCardCodes.size > 0) {
                const cards = await stockDepotCardsApi.load({ flt: { Codes: [...stockDepotCardCodes] } }, config);
                cards.forEach((card) => stockDepotCards.set(card.Code, card));
            }

            if (shouldIncludeLeaveCard && wsDepotCardCodes.size > 0) {
                const cards = await wsDepotCardsServices.load(
                    {
                        flt: { Codes: [...wsDepotCardCodes] },
                        include: includeLeaveCard,
                    },
                    config,
                );
                cards.forEach((card: WsDepotCard) => wsDepotCards.set(card.Code, card));
            }

            if (includeOrder && orderCodes.size > 0) {
                const loadedOrders = await ordersApi.load({ flt: { Codes: [...orderCodes] } }, config);
                loadedOrders.forEach((order) => orders.set(order.Code, order));
            }

            return inpContents.map<WsInplantContent>((inp) =>
                serializeContent(inp, {
                    LeaveCard: shouldIncludeLeaveCard ? wsDepotCards.get(inp.LeaveCardCode) : undefined,
                    ArrivalCard: includeArrivalCard
                        ? inp.ArrivalCardType === DepotCardType.Stock
                            ? (stockDepotCards.get(inp.ArrivalCardCode) ?? null)
                            : (wsDepotCards.get(inp.ArrivalCardCode) ?? null)
                        : undefined,
                    Order: includeOrder ? (orders.get(inp.OrderCode) ?? null) : undefined,
                }),
            );
        },

        /** Возвращает одну строку состава ВЗН по фильтру. */
        loadOne: async (
            params: {
                flt: WsInplantContentFilter;
                include?: Array<keyof WsInplantContentExtends>;
                includeLeaveCard?: Array<keyof WsDepotCardExtends>;
            },
            config?: RequestConfig,
        ): Promise<WsInplantContent | null> => {
            const items = await wsInplantsServices.content.load(
                {
                    ...params,
                },
                config,
            );

            return items[0] ?? null;
        },
    },
} as const;

function serializeInplant(apiInplant: ApiWsInplant, includeData?: Partial<WsInplantExtends>): WsInplant {
    return {
        Code: apiInplant.Code,
        Num: apiInplant.Num,
        DocDate: apiInplant.DocDate,
        LeaveMoveDate: apiInplant.LeaveMoveDate,
        ArrivalMoveDate: apiInplant.ArrivalMoveDate,
        SenderCode: apiInplant.Sender,
        Sender: includeData?.Sender,
        SenderSectionCode: apiInplant.SenderSection,
        SenderSection: includeData?.SenderSection,
        ReceiverCode: apiInplant.Receiver,
        Receiver: includeData?.Receiver,
        ReceiverSectionCode: apiInplant.ReceiverSection,
        ReceiverSection: includeData?.ReceiverSection,
        Bo: includeData?.Bo,
    };
}

function serializeContent(apiData: ApiWsInplantContent, includeData?: Partial<WsInplantContentExtends>): WsInplantContent {
    return {
        Code: apiData.Code,
        InplantCode: apiData.InplantCode,
        LeaveCardCode: apiData.LeaveCardCode,
        LeaveCard: includeData?.LeaveCard,
        LotCode: apiData.LotCode,
        ArrivalCardCode: apiData.ArrivalCardCode,
        ArrivalCard: includeData?.ArrivalCard,
        ArrivalCardType: apiData.ArrivalCardType,
        LeaveQty: apiData.LeaveQty,
        ArrivalQty: apiData.ArrivalQty,
        OrderCode: apiData.OrderCode,
        Order: includeData?.Order,
        ArticleOfExpense: apiData.ArticleOfExpense,
        LeaveSdbCode: apiData.LeaveSdbCode,
        ArrivalSdbCode: apiData.ArrivalSdbCode,
    };
}
